import React from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import styles from 'styles/pages/soeezblog-slug.module.scss';
import getPosts from 'lib/getPosts';
import getBrandsModels from 'lib/getBrandsModels';
import { apiWp } from 'lib/functions';
import BlogImage from 'components/blogImage';
import BlogVideo from 'components/blogVideo';
import BlogPost from 'components/blogPost';
import Loading from 'components/loading';
import Breadcrumb from 'components/breadcrumb';
import Link from 'components/link';

const WidgetNav = dynamic(() => import('../../components/widgetNav'), {
    ssr: false,
});

const WidgetLaunches = dynamic(() => import('../../components/widgetLaunches'), {
    ssr: false,
});

const WidgetPromo = dynamic(() => import('../../components/widgetPromotion'), {
    ssr: false,
});

const Post = ({ post, postFormat, brands }) => {
    const router = useRouter();
    if (!post || !brands || router.isFallback) {
        return <Loading />;
    }
    return (
        <div>
            <Head>
                <title>{`${post.title} - Maroc`}</title>
                <meta name="description" content={post.excerpt} />
                <meta property="og:type" content="article" />
                <meta property="og:title" content={post.title} />
                <meta property="og:description" content={post.excerpt} />
                <meta
                    property="og:image"
                    content={`${process.env.NEXT_PUBLIC_API_HOST}/images/og/blog.png`}
                />
                <meta
                    property="og:url"
                    content={`${process.env.NEXT_PUBLIC_CLIENT_HOST}${router.asPath}`}
                />
            </Head>
            <main className={styles.root}>
                <Breadcrumb
                    links={[
                        {
                            href: '/soeez-blog',
                            text: 'accueil blog',
                        },
                        {
                            href: null,
                            text: post.title,
                        },
                    ]}
                />
                <div className="main-title">
                    <h1>{post.title}</h1>
                </div>
                <div className={styles.mainContainer}>
                    <div>
                        <article className={styles.article}>
                            {postFormat === 'Image' && <BlogImage post={post} />}
                            {postFormat === 'Vidéo' && <BlogVideo post={post} />}
                            {postFormat === 'Standard' && <BlogPost post={post} />}
                        </article>
                        <div className={styles.navLink}>
                            {post?.previous && (
                                <Link href={`/soeez-blog/${post.previous.slug}`}>
                                    <span>Article précédent:</span>
                                    {post.previous.title}
                                </Link>
                            )}
                            {post?.next && (
                                <Link href={`/soeez-blog/${post.next.slug}`}>
                                    <span>Article suivant:</span>
                                    {post.next.title}
                                </Link>
                            )}
                        </div>
                    </div>
                    <div>
                        <WidgetNav brands={brands} />
                        <WidgetLaunches data={brands} />
                        <WidgetPromo data={brands} />
                    </div>
                </div>
            </main>
        </div>
    );
};

Post.propTypes = {
    post: PropTypes.any,
    postFormat: PropTypes.any,
    brands: PropTypes.any,
};

export default Post;

const queryQl = `query getPost($slug: ID!) {
    post(idType: SLUG, id: $slug) {
        date
        excerpt
        title
        postFormats {
            nodes {
                name
            }
        }
        content
        next {
            slug
            title
        }
        previous {
            slug
            title
        }
    }
}`;

export async function getStaticPaths() {
    let posts = await getPosts();
    posts = posts.data.posts;
    const paths = [];
    posts.edges.forEach((post) => {
        paths.push({
            params: {
                slug: post.node.slug,
            },
        });
    });
    paths.push({
        params: {
            slug: 'les-10-voitures-moins-cheres-en-promotion-au-maroc',
        },
    });
    return {
        paths,
        fallback: true,
    };
}

export async function getStaticProps({ params }) {
    const variables = {
        slug: params.slug,
    };
    let post = await apiWp(queryQl, variables);
    if (!post.data?.post || !post.data?.post?.content) {
        return {
            notFound: true,
        };
    }
    const getPostFormat = () => {
        if (post.postFormats.nodes.length === 0) {
            return 'Standard';
        }
        return post.postFormats.nodes[0].name;
    };

    post = post.data.post;
    let brands = await getBrandsModels();
    brands = brands.data.brands;

    return {
        props: {
            post,
            postFormat: getPostFormat(),
            brands,
        },
    };
}
